import { Outlet, NavLink } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { hasPermission } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { LayoutDashboard, Users, Package, FileText, UserCog, Settings, BarChart3, History, LogOut, Menu, X } from 'lucide-react'
import { useState } from 'react'
import logoUrl from '@/assets/logo.png'
import type { LucideIcon } from 'lucide-react'
import type { Permission, User } from '@/api/types'

interface NavItemDef {
  name: string
  icon: LucideIcon
  path?: string
  onClick?: () => void
  isDestructive?: boolean
  disabled?: boolean
  /** Krävs för att länken ens ska synas. Saknas permission göms den helt. */
  permissions?: Permission[]
}

function isVisible(item: NavItemDef, user: User | null): boolean {
  return hasPermission(user, ...(item.permissions ?? []))
}

const itemBaseClass = `
  flex justify-center lg:justify-start px-3 py-4 lg:py-2.5 rounded-lg
  w-[90%] max-w-sm mx-auto lg:max-w-none lg:w-full lg:mx-0
  text-[clamp(1rem,6vw,1.375rem)] lg:text-base
`

const disabledClass = 'opacity-40 cursor-not-allowed bg-white/60 shadow-sm lg:bg-transparent lg:shadow-none text-muted-foreground'

function NavItemContent({ icon: Icon, name }: { icon: LucideIcon; name: string }): React.ReactElement {
  return (
    <span className="flex items-center gap-6 lg:gap-3 w-[70%] lg:w-full">
      <Icon className="h-7 w-7 lg:h-5 lg:w-5 shrink-0" />
      <span className="text-left">{name}</span>
    </span>
  )
}

function NavItemRow({ item, onNavigate }: { item: NavItemDef; onNavigate: () => void }): React.ReactElement {
  const isLink = !!item.path
  const isDestructive = !!item.isDestructive
  const isDisabled = !!item.disabled

  return (
    <>
      {isDisabled && (
        <span className={`${itemBaseClass} ${disabledClass}`}>
          <NavItemContent
            icon={item.icon}
            name={item.name}
          />
        </span>
      )}
      {!isDisabled && isLink && (
        <NavLink
          to={item.path!}
          onClick={onNavigate}
          className={({ isActive }) =>
            `${itemBaseClass} ${
              isActive
                ? 'bg-primary/10 text-primary font-semibold lg:text-lg'
                : 'bg-white/60 shadow-sm lg:bg-transparent lg:shadow-none text-muted-foreground hover:bg-primary/5 hover:text-foreground active:bg-primary/5 active:text-foreground'
            }`
          }
        >
          <NavItemContent
            icon={item.icon}
            name={item.name}
          />
        </NavLink>
      )}
      {!isDisabled && !isLink && (
        <button
          onClick={item.onClick}
          aria-label={item.name}
          title={item.name}
          className={`${itemBaseClass} ${
            isDestructive
              ? 'bg-white/60 shadow-sm lg:bg-transparent lg:shadow-none text-muted-foreground hover:bg-destructive/10 hover:text-destructive active:bg-destructive/10 active:text-destructive transition-colors'
              : 'bg-white/60 shadow-sm lg:bg-transparent lg:shadow-none text-muted-foreground hover:bg-primary/5 hover:text-foreground'
          }`}
        >
          <NavItemContent
            icon={item.icon}
            name={item.name}
          />
        </button>
      )}
    </>
  )
}

export function AppLayout(): React.ReactElement {
  const logout = useAuthStore((state) => state.logout)
  const user = useAuthStore((state) => state.user)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const canOpenSystem = hasPermission(user, 'invoicing.read', 'invoicing.run')

  const allNavItems: NavItemDef[] = [
    { name: 'Översikt', path: '/', icon: LayoutDashboard, permissions: ['reports.read'] },
    { name: 'Kunder', path: '/customers', icon: Users, permissions: ['customers.read'] },
    { name: 'Leveranser', path: '/deliveries', icon: Package, permissions: ['shipments.read'] },
    { name: 'Fakturor', path: '/invoices', icon: FileText, permissions: ['invoicing.read'] },
    { name: 'Användare', path: '/users', icon: UserCog, permissions: ['accounts.manage'] },
    { name: 'Historik', path: '/historik', icon: History, permissions: ['history.write'] },
    { name: 'System', path: '/system', icon: Settings, disabled: !canOpenSystem },
    { name: 'Rapporter', icon: BarChart3, disabled: true },
    { name: 'Logga ut', icon: LogOut, onClick: logout, isDestructive: true }
  ]

  const navItems = allNavItems.filter((item) => isVisible(item, user))

  const handleNavigate = (): void => setIsMobileMenuOpen(false)

  return (
    <div className="min-h-screen bg-background flex font-sans transition-colors duration-300">
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
        fixed lg:static inset-y-0 left-0 z-40 w-full lg:w-64 transform
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-[#f2f6f4] lg:border-r border-border flex flex-col shadow-2xl lg:shadow-none
      `}
      >
        <div className="h-24 flex items-center justify-center px-6 bg-primary shrink-0 relative">
          <img
            src={logoUrl}
            alt="Hälsogruppen Logo"
            className="h-18 lg:h-14 w-auto object-contain"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden absolute right-4 size-12 text-primary-foreground hover:bg-white/20 active:bg-white/20"
          >
            <X className="size-8" />
          </Button>
        </div>

        <nav className="flex-1 px-4 mt-8 space-y-4 overflow-y-auto">
          {navItems.map((item) => (
            <NavItemRow
              key={item.name}
              item={item}
              onNavigate={handleNavigate}
            />
          ))}
        </nav>

        <div className="p-4 border-t border-border shrink-0">
          <div className="flex items-center gap-4 px-2">
            <div className="h-14 w-14 lg:h-9 lg:w-9 rounded-full bg-muted flex items-center justify-center text-xl lg:text-sm font-medium shrink-0">
              {user?.email?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base lg:text-sm font-medium text-foreground truncate">{user?.email}</p>
              <p className="text-sm lg:text-xs text-muted-foreground truncate">Administratör</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground/50 text-center mt-3">Fas 3</p>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden h-screen bg-[#fcfcfb]">
        <header className="h-20 shrink-0 sticky top-0 z-30 flex items-center px-4 lg:px-10 bg-primary lg:bg-white/80 lg:backdrop-blur-md lg:border-b lg:border-border">
          <div className="flex lg:hidden items-center justify-between w-full">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(true)}
              className="size-12 text-primary-foreground hover:bg-white/20 active:bg-white/20"
            >
              <Menu className="size-8" />
            </Button>
            <img
              src={logoUrl}
              alt="Hälsogruppen Logo"
              className="h-14 w-auto object-contain"
            />
            <div className="size-12 shrink-0" />
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6 lg:p-10 relative">
          <div className="max-w-7xl mx-auto w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  )
}
